"use client";

import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, FileText, FolderKanban, Plus, Siren, Ticket } from "lucide-react";
import { ArticleTypeModal } from "@/components/ArticleTypeModal";
import { SupportTicketIntake } from "@/components/SupportTicketIntake";
import { LogTimeButton } from "@/components/bexio/time/LogTimeButton";
import { newMenuItems } from "@/lib/new-menu";
import { openNewProjectDialog } from "@/lib/new-project-dialog";

type Option = { id: number; name: string };

const ROW = "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-fg-soft hover:bg-surface-muted";

/**
 * "New" in the navbar: one place to start a time entry, ticket, article, escalation or project, filtered by what the person may create.
 * The list stays mounted while closed so the dialogs that rows open keep their state.
 */
export function NewMenu({ permissions, clients, applications }: { permissions: string[]; clients: Option[]; applications: Option[] }) {
  const items = newMenuItems(permissions);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (items.length === 0) return null;
  const close = () => setOpen(false);
  const row = (icon: ReactNode, label: string) => <span className={ROW}>{icon}{label}</span>;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="New"
        onClick={() => setOpen((o) => !o)}
        className="gradient-button mr-1 inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 sm:pl-3.5 sm:pr-3 text-sm font-medium text-white shadow-sm transition-[opacity,box-shadow] hover:opacity-95 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
      >
        <Plus size={16} aria-hidden /> <span className="hidden sm:inline">New</span>
        <ChevronDown size={14} aria-hidden className={`hidden opacity-80 sm:block transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div role="menu" aria-label="Create new" hidden={!open} className="absolute right-0 top-full z-overlay mt-1 w-52 overflow-hidden rounded-md border border-line bg-surface py-1 shadow-xl">
        {items.map((key) => {
          switch (key) {
            case "time":
              return (
                <div key={key} role="none" onClick={close}>
                  <LogTimeButton variant="ghost" size="md" label="Log time" className={`${ROW} h-auto justify-start rounded-none font-normal`} />
                </div>
              );
            case "ticket":
              return (
                <div key={key} role="none" onClick={close}>
                  <SupportTicketIntake trigger="custom" clients={clients} applications={applications}>{row(<Ticket size={16} aria-hidden />, "Ticket")}</SupportTicketIntake>
                </div>
              );
            case "article":
              return (
                <div key={key} role="none" onClick={close}>
                  <ArticleTypeModal trigger="custom">{row(<FileText size={16} aria-hidden />, "Article")}</ArticleTypeModal>
                </div>
              );
            case "escalation":
              return <Link key={key} role="menuitem" href="/escalations/new" onClick={close} className={ROW}><Siren size={16} aria-hidden /> Escalation</Link>;
            case "project":
              return (
                <button key={key} type="button" role="menuitem" onClick={() => { close(); openNewProjectDialog(); }} className={ROW}>
                  <FolderKanban size={16} aria-hidden /> Project
                </button>
              );
          }
        })}
      </div>
    </div>
  );
}

